'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { useLoginFlow } from '@/components/auth/use-login-flow';
import { FormError } from '@/components/form-error';
import { FormInfo } from '@/components/form-success';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Heading } from '@/components/ui/Heading';
import { Input } from '@/components/ui/input';
import { Paragraph } from '@/components/ui/texts/paragraph';
import { getAuthRedirectURLFromSearchParams, withNextParam } from '@/lib/auth-redirect';
import { cn } from '@/lib/utils';
import { Button } from '@/ui-lib/button';

const inputClass =
    'w-full rounded-[16px] md:rounded-[18px] p-4 ' +
    'bg-transparent border border-bg-pricing-stroke placeholder:text-neutrals-300 ' +
    'focus:outline-none focus:ring-2 focus:ring-[#1e2416]/30 focus:border-[#1e2416]';

type LoginComplete = {
    redirectUrl: string;
};

export const LoginForm = ({
    onLoginComplete,
    defaultEmail,
    noSignupLink = true,
    lockedEmail = false,
}: {
    onLoginComplete?: (options: LoginComplete) => void;
    defaultEmail?: string;
    noSignupLink?: boolean;
    lockedEmail?: boolean;
}) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const nextURL = getAuthRedirectURLFromSearchParams(searchParams);

    const loginFlow = useLoginFlow({
        defaultEmail,
        lockedEmail,
        next: nextURL,
        onLoginSuccess: async ({ redirectUrl }) => {
            if (onLoginComplete) {
                onLoginComplete({ redirectUrl });
            } else {
                router.push(redirectUrl);
            }
        },
        onPasswordExpired: (email) => {
            router.push('/expired-password?email=' + email);
        },
    });
    const otpVerifyEmail = loginFlow.otpVerifyForm.watch('email');

    if (loginFlow.mode === 'otp-request') {
        let emailProperties = {};
        if (lockedEmail) {
            emailProperties = {
                'data-lpignore': true,
                'data-1p-ignore': true,
                autoComplete: 'off',
                autoCapitalize: 'none',
                inputMode: 'email',
            };
        } else {
            emailProperties = {};
        }

        return (
            <div className="h-full flex flex-col " key={'otp-request'}>
                <Heading styleVariant={'subHeadline'} typeVariant={'h2'} className="text-grey-900">
                    Enter your email address
                </Heading>
                <Paragraph variant="body2" className="!text-neutral-900 pt-4 font-neue-haas">
                    We&apos;ll send a one-time password so you can log in without your password.
                </Paragraph>
                <Form analyticsTrackingName="Login OTP Request" {...loginFlow.otpRequestForm}>
                    <form
                        onSubmitCapture={loginFlow.syncOtpRequestAutofillOnSubmit}
                        onSubmit={loginFlow.otpRequestForm.handleSubmit((values) => loginFlow.submitOtpRequest(values))}
                        className="w-full h-full"
                    >
                        <div className="flex flex-col w-full pt-6">
                            {lockedEmail && (
                                <div className="p-3 border-neutral-100 border border-solid text-neutral-300 text-sm opacity-70 rounded-md">
                                    {defaultEmail}
                                </div>
                            )}
                            {!lockedEmail && (
                                <FormField
                                    control={loginFlow.otpRequestForm.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 w-full">
                                            <FormControl>
                                                <label>
                                                    <span
                                                        className={
                                                            'block mb-2 font-semibold text-[16px] md:text-[16px] text-text-paragraph-dark'
                                                        }
                                                    >
                                                        Email
                                                    </span>
                                                    <input
                                                        className={inputClass}
                                                        {...field}
                                                        ref={loginFlow.attachInputRef('otpRequestEmail', field.ref)}
                                                        disabled={
                                                            loginFlow.otpRequestForm.formState.isSubmitting ||
                                                            lockedEmail
                                                        }
                                                        placeholder="john.doe@example.com"
                                                        type="email"
                                                        autoComplete={'username'}
                                                        autoFocus
                                                    />
                                                </label>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormError message={loginFlow.otpRequestForm.formState.errors.root?.message} />
                            <Button
                                disabled={loginFlow.otpRequestForm.formState.isSubmitting}
                                type="submit"
                                mode={'light'}
                                className="w-full mt-4"
                            >
                                Send code
                            </Button>
                            <Button
                                type="button"
                                mode={'light'}
                                variant="tertiary"
                                onClick={loginFlow.switchToPassword}
                                disabled={loginFlow.otpRequestForm.formState.isSubmitting}
                                className="w-full mt-2"
                            >
                                Login with password instead
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        );
    }

    if (loginFlow.mode === 'otp-verify') {
        return (
            <div className="h-full flex flex-col pb-4" key={'otp-verify'}>
                <Heading styleVariant={'subHeadline'} typeVariant={'h2'} className="text-grey-900 pt-10 ">
                    Enter one-time password
                </Heading>
                <Paragraph variant="body2" className="!text-neutral-900 pt-4 font-neue-haas">
                    Enter the 6-digit code sent to {otpVerifyEmail || 'your email address'}.
                </Paragraph>

                <Form analyticsTrackingName="Login OTP Verify" {...loginFlow.otpVerifyForm}>
                    <form
                        key="otp-verify-form"
                        // onSubmitCapture={loginFlow.syncOtpCodeAutofillOnSubmit}
                        onSubmit={loginFlow.otpVerifyForm.handleSubmit(loginFlow.submitOtpVerify)}
                        className="w-full h-full p-"
                        autoComplete="off"
                    >
                        <input
                            type="email"
                            name="email"
                            value={otpVerifyEmail ?? ''}
                            autoComplete="username"
                            readOnly
                            tabIndex={-1}
                            aria-hidden="true"
                            className="sr-only"
                        />
                        <input type="hidden" {...loginFlow.otpVerifyForm.register('challengeId')} />
                        <div className="flex flex-col w-full pt-6">
                            <FormField
                                control={loginFlow.otpVerifyForm.control}
                                name="code"
                                render={({ field }) => (
                                    <FormItem className="flex-1 w-full">
                                        <FormControl>
                                            <Input
                                                {...field}
                                                name="one-time-code"
                                                id="one-time-code"
                                                className={cn(
                                                    inputClass,
                                                    'text-neutral-900 text-center tracking-[0.3em] co'
                                                )}
                                                ref={loginFlow.attachInputRef('otpVerifyCode', field.ref)}
                                                disabled={loginFlow.otpVerifyForm.formState.isSubmitting}
                                                placeholder="123456"
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                autoCapitalize="none"
                                                autoCorrect="off"
                                                data-lpignore="true"
                                                data-1p-ignore="true"
                                                spellCheck={false}
                                                autoFocus
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="pt-3 flex items-center justify-between gap-3">
                                <Button
                                    type="button"
                                    variant="text"
                                    onClick={loginFlow.resendOtpCode}
                                    mode={'light'}
                                    disabled={loginFlow.otpVerifyForm.formState.isSubmitting}
                                    textClassName={'hover:underline nowrap'}
                                    className={'block '}
                                >
                                    Resend code
                                </Button>
                                {lockedEmail ? (
                                    <></>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="text"
                                        mode={'light'}
                                        onClick={loginFlow.switchToOtpEmail}
                                        disabled={loginFlow.otpVerifyForm.formState.isSubmitting}
                                        textClassName={'hover:underline nowrap'}
                                    >
                                        Edit email
                                    </Button>
                                )}
                            </div>
                            <FormInfo message={loginFlow.infoMessage} />
                            <FormError message={loginFlow.otpVerifyForm.formState.errors.root?.message} />
                            <Button
                                disabled={loginFlow.otpVerifyForm.formState.isSubmitting}
                                type="submit"
                                className="w-full mt-4"
                                mode={'light'}
                            >
                                Verify code
                            </Button>
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={loginFlow.switchToPassword}
                                disabled={loginFlow.otpVerifyForm.formState.isSubmitting}
                                className="w-full mt-2"
                                mode={'light'}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col pb-4" key={'password'}>
            <Heading styleVariant={'subHeadline'} typeVariant={'h2'} className="text-grey-900">
                Welcome Back
            </Heading>
            {!noSignupLink && (
                <Paragraph variant="body2" className="!text-neutral-900 pt-4 font-neue-haas">
                    Don&#39;t have an account yet?&nbsp;
                    <Link
                        className="!text-nInfo-500 pl-1"
                        href={withNextParam('/register', nextURL)}
                        data-testid="switch-to-register"
                    >
                        Sign up
                    </Link>
                    .
                </Paragraph>
            )}
            <Form analyticsTrackingName="Login" {...loginFlow.passwordForm}>
                <form
                    onSubmitCapture={loginFlow.syncPasswordAutofillOnSubmit}
                    onSubmit={loginFlow.passwordForm.handleSubmit(loginFlow.submitPasswordLogin)}
                    className="w-full h-full"
                >
                    <div className="flex flex-col w-full pt-6">
                        <FormField
                            control={loginFlow.passwordForm.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem className="flex-1 w-full">
                                    <FormControl>
                                        <label>
                                            <span
                                                className={
                                                    'block mb-2 font-semibold text-[16px] md:text-[16px] text-text-paragraph-dark'
                                                }
                                            >
                                                Email
                                            </span>
                                            <input
                                                className={cn(
                                                    inputClass,
                                                    lockedEmail ? 'border-neutral-100 text-neutral-300' : ''
                                                )}
                                                {...field}
                                                ref={loginFlow.attachInputRef('passwordEmail', field.ref)}
                                                disabled={loginFlow.passwordForm.formState.isSubmitting || lockedEmail}
                                                placeholder="john.doe@example.com"
                                                type="email"
                                                autoComplete={'username'}
                                                autoFocus
                                            />
                                        </label>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={loginFlow.passwordForm.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem className="flex-1 w-full pt-4">
                                    <FormControl>
                                        <label>
                                            <span className="block mb-2 font-semibold text-[16px] md:text-[16px] text-text-paragraph-dark">
                                                Password
                                            </span>
                                            <input
                                                className={inputClass}
                                                {...field}
                                                ref={loginFlow.attachInputRef('password', field.ref)}
                                                disabled={loginFlow.passwordForm.formState.isSubmitting}
                                                placeholder="******"
                                                type="password"
                                                autoComplete="current-password"
                                            />
                                        </label>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Link
                            href={{
                                pathname: '/recover',
                                query: { email: loginFlow.passwordForm.getValues('email') },
                            }}
                            className="my-1"
                        >
                            <Button
                                variant="text"
                                className="text-center block py-1 w-full hover:text-neutral-900 hover:underline"
                                textClassName="!text-nInfo-500 hover:text-neutral-900 hover:underline font-neue-haas"
                            >
                                Forgot password?
                            </Button>
                        </Link>

                        <FormError message={loginFlow.passwordForm.formState.errors.root?.message} />

                        <div className="flex flex-wrap gap-3 w-full mt-4">
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={loginFlow.switchToOtpRequest}
                                disabled={loginFlow.passwordForm.formState.isSubmitting}
                                mode="light"
                                className="flex-1 wrap-normal"
                                textClassName="font-neue-haas text-sm"
                            >
                                Login with one-time code
                            </Button>
                            <Button
                                disabled={loginFlow.passwordForm.formState.isSubmitting}
                                type="submit"
                                variant="primary"
                                mode="light"
                                className="flex-1 wrap-normal"
                                textClassName="font-neue-haas text-sm"
                            >
                                Login with password
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>
        </div>
    );
};
